/**
 * 이미지 압축 및 WebP 변환 유틸리티
 */

/**
 * 이미지를 압축하고 WebP로 변환
 * @param {File} file - 원본 이미지 파일
 * @param {Object} options - 옵션
 * @param {number} options.maxWidth - 최대 너비 (기본: 1920)
 * @param {number} options.maxHeight - 최대 높이 (기본: 1080)
 * @param {number} options.quality - 품질 0-1 (기본: 0.8)
 * @returns {Promise<Blob>} - WebP Blob
 */
export const compressAndConvertToWebP = (file, options = {}) => {
  const { maxWidth = 1920, maxHeight = 1080, quality = 0.8 } = options

  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      const img = new Image()

      img.onload = () => {
        // 캔버스 생성
        const canvas = document.createElement('canvas')
        let { width, height } = img

        // 비율 유지하면서 리사이즈
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height)
          width = Math.round(width * ratio)
          height = Math.round(height * ratio)
        }

        canvas.width = width
        canvas.height = height

        // 이미지 그리기
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)

        // WebP로 변환
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob)
            } else {
              reject(new Error('이미지 변환에 실패했습니다.'))
            }
          },
          'image/webp',
          quality
        )
      }

      img.onerror = () => {
        reject(new Error('이미지를 불러올 수 없습니다.'))
      }

      img.src = e.target.result
    }

    reader.onerror = () => {
      reject(new Error('파일을 읽을 수 없습니다.'))
    }

    reader.readAsDataURL(file)
  })
}

/**
 * 썸네일 생성 (작은 이미지)
 * @param {File} file - 원본 이미지 파일
 * @param {number} size - 썸네일 크기 (기본: 400)
 * @returns {Promise<Blob>} - WebP Blob
 */
export const createThumbnail = (file, size = 400) => {
  return compressAndConvertToWebP(file, {
    maxWidth: size,
    maxHeight: size,
    quality: 0.7
  })
}

/**
 * 파일 크기를 읽기 좋은 형태로 변환
 * @param {number} bytes - 바이트
 * @returns {string} - "1.5 MB" 형태
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * 이미지 파일인지 확인
 * @param {File} file - 파일
 * @returns {boolean}
 */
export const isImageFile = (file) => {
  const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp']
  return validTypes.includes(file.type)
}

/**
 * WebP 파일명 생성
 * @param {string} originalName - 원본 파일명
 * @returns {string} - .webp 확장자 파일명
 */
export const generateWebPFileName = (originalName) => {
  const nameWithoutExt = originalName.replace(/\.[^/.]+$/, '')
  const timestamp = Date.now()
  return `${nameWithoutExt}_${timestamp}.webp`
}
